/**
 * Headless world state. No PixiJS. See CLAUDE.md architecture rule 4.
 *
 * (x, y) is the logical integer tile — the source of truth for gameplay
 * (collision, sight, sound, AI). (visualX, visualY) is a derived float in
 * tile units that lags behind during a step; the renderer lerps between
 * `prevVisualX/Y` and `visualX/Y` using the FixedLoop's alpha. See
 * `syncVisual` in `movement.ts`.
 */

export type EntityId = number;

export interface Entity {
  id: EntityId;
  glyph: string;
  /** 24-bit RGB, e.g. 0xffcc33 */
  fg: number;
  /** 24-bit RGB. Set negative to skip the background quad. */
  bg: number;
  /** Logical integer tile position. */
  x: number;
  y: number;
  /** Direction of the currently-in-progress movement, in {-1, 0, 1}. */
  moveDx: number;
  moveDy: number;
  /** Orientation the entity is *looking* (TORSO), in {-1, 0, 1} per axis
   *  (8-way). Independent of movement — the renderer rotates the torso glyph so
   *  its top points this way, and it drives the vision cone. Player aims with
   *  the arrow keys; guards face their patrol/investigation/combat target. */
  facingDx: number;
  facingDy: number;
  /** Orientation of the LEGS (movement), in {-1, 0, 1} per axis (8-way).
   *  Retained while idle so the legs glyph keeps pointing where the entity last
   *  walked. Player: camera-relative WASD latch (movement.ts). Guard: latched
   *  to the last tile-step direction (guard.ts). Renderer rotates the legs
   *  glyph to this. */
  legDx: number;
  legDy: number;
  /** Progress within the current one-tile step, in [0, 1]. */
  moveProgress: number;
  /** Derived visual position in tile units — updated after every logical change. */
  visualX: number;
  visualY: number;
  /** Snapshot of visualX/Y at the start of the current tick. */
  prevVisualX: number;
  prevVisualY: number;
  /** 0 = upright, 1 = fully on their side. Advances toward 1 while
   *  incapacitated/dead, decays back toward 0 during recovery. Renderer
   *  reads this to rotate the glyph up to ±90°. */
  tilt: number;
  /** Attack recoil / hit shake: nonzero for a few frames after damage. */
  hitShake: number;
  /** Attack skew: >0 when swinging in +x direction, <0 for -x. Decays. */
  attackSkew: number;
  /** Muzzle-flash / strike-flash tint pulse timer in seconds. */
  flashTimer: number;
  /** Blood carried on shoes/paws; drops fractionally at each new tile. */
  bloodOnFeet: number;
  /** Flash-blind timer (seconds remaining). >0 = dazed + whited-out vision;
   *  the first `CONFIG.blind.stunSec` of it also freezes movement. See
   *  sim/blind.ts. Applies to the player and every NPC. */
  blindTimer: number;
  /** True for the janitor — a non-combatant, so the renderer skips the red
   *  damage glow and combat/debug labels it a "janitor", not a guard. */
  isJanitor?: boolean;
}

export type EntityInit = Omit<
  Entity,
  | 'id'
  | 'moveDx'
  | 'moveDy'
  | 'facingDx'
  | 'facingDy'
  | 'legDx'
  | 'legDy'
  | 'moveProgress'
  | 'visualX'
  | 'visualY'
  | 'prevVisualX'
  | 'prevVisualY'
  | 'tilt'
  | 'hitShake'
  | 'attackSkew'
  | 'flashTimer'
  | 'bloodOnFeet'
  | 'blindTimer'
>;

export class World {
  readonly entities: Map<EntityId, Entity> = new Map();
  ticks = 0;
  private nextId: EntityId = 1;

  spawn(init: EntityInit): Entity {
    const e: Entity = {
      id: this.nextId++,
      moveDx: 0,
      moveDy: 0,
      facingDx: 0,
      facingDy: -1,
      legDx: 0,
      legDy: -1,
      moveProgress: 0,
      visualX: init.x,
      visualY: init.y,
      prevVisualX: init.x,
      prevVisualY: init.y,
      tilt: 0,
      hitShake: 0,
      attackSkew: 0,
      flashTimer: 0,
      bloodOnFeet: 0,
      blindTimer: 0,
      ...init,
    };
    this.entities.set(e.id, e);
    return e;
  }

  /** Snapshot visualX/Y → prevVisualX/Y. Call at the start of every tick. */
  snapshotPrev(): void {
    for (const e of this.entities.values()) {
      e.prevVisualX = e.visualX;
      e.prevVisualY = e.visualY;
    }
  }
}
